// ConfirmPH: Reviews moderation & business reply dashboard.
//
// Owner-only view that lists every review for the company (any status),
// lets the owner publish/hide/reject/flag, and add or edit the official
// business reply on a published review.
//
// Backend endpoints used (see backend/discovery/staff_router.py):
//   GET    /api/reviews?status=...       : list + status counts
//   POST   /api/reviews/{id}/moderate    : change status
//   POST   /api/reviews/{id}/reply       : add/update business reply
//   DELETE /api/reviews/{id}/reply       : remove the reply
import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  Star, ShieldCheck, Eye, EyeOff, Ban, Flag, MessageSquare, Trash2,
  Loader2, ExternalLink,
} from "lucide-react";
import { Link } from "react-router-dom";
import TrustBadgesPanel from "@/components/TrustBadgesPanel";

const STATUS_FILTERS = [
  { key: "all",       label: "All" },
  { key: "pending",   label: "Pending" },
  { key: "published", label: "Published" },
  { key: "hidden",    label: "Hidden" },
  { key: "rejected",  label: "Rejected" },
  { key: "flagged",   label: "Flagged" },
];

const STATUS_TONES = {
  published: "bg-emerald-100 text-emerald-800 border-emerald-200",
  pending:   "bg-amber-100  text-amber-800  border-amber-200",
  hidden:    "bg-slate-200  text-slate-700  border-slate-300",
  rejected:  "bg-rose-100   text-rose-800   border-rose-200",
  flagged:   "bg-orange-100 text-orange-800 border-orange-200",
};

export default function ReviewsDashboard() {
  const { user } = useAuth();
  const isOwner = (user?.account_type || user?.role) === "business_owner"
                  || (user?.account_type || user?.role) === "platform_admin";
  const [filter, setFilter] = useState("all");
  const [data, setData] = useState({ items: [], counts: {} });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const { data: res } = await api.get("/reviews", { params });
      setData(res);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  if (!isOwner) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-amber-900 max-w-2xl"
           data-testid="reviews-no-access">
        Only the business owner can moderate reviews and reply to customers.
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="reviews-dashboard">
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold inline-flex items-center gap-2">
            <Star className="w-6 h-6 text-amber-500" /> Reviews
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Reviews are submitted by customers through a secure invite link sent
            when a booking is completed. Approve, hide, or reject, and post your
            official reply.
          </p>
        </div>
      </header>

      <TrustBadgesPanel />

      <div className="flex flex-wrap gap-2" data-testid="reviews-filter-bar">
        {STATUS_FILTERS.map((f) => {
          const n = f.key === "all"
            ? (data.counts?.all || 0)
            : (data.counts?.[f.key] || 0);
          const active = filter === f.key;
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              data-testid={`reviews-filter-${f.key}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                active
                  ? "bg-[#1B4332] text-white border-[#1B4332]"
                  : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
              }`}
            >
              {f.label} <span className="ml-1 opacity-70">({n})</span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="py-10 text-center text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading reviews…
        </div>
      ) : data.items.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-8 text-center text-sm text-slate-600"
             data-testid="reviews-empty">
          No reviews in this view yet.
        </div>
      ) : (
        <div className="space-y-4" data-testid="reviews-list">
          {data.items.map((r) => (
            <ReviewCard key={r.id} review={r} onChange={load} />
          ))}
        </div>
      )}

      <p className="text-xs text-slate-500">
        Tip: Replies should be professional and on-topic. Threats, doxxing, or
        contact details of other customers will be removed.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Review card with moderation buttons + reply editor.
// ---------------------------------------------------------------------------
function ReviewCard({ review, onChange }) {
  const [busy, setBusy] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState(review.business_reply || "");

  const moderate = async (status, reason) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/reviews/${review.id}/moderate`, { status, reason });
      toast.success(`Review marked as ${status}`);
      await onChange();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const saveReply = async () => {
    const text = replyText.trim();
    if (text.length < 2) {
      toast.error("Reply must be at least 2 characters.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/reviews/${review.id}/reply`, { text });
      toast.success(review.business_reply ? "Reply updated" : "Reply posted");
      setReplyOpen(false);
      await onChange();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const removeReply = async () => {
    if (!window.confirm("Remove the business reply on this review?")) return;
    setBusy(true);
    try {
      await api.delete(`/reviews/${review.id}/reply`);
      toast.success("Reply removed");
      setReplyText("");
      setReplyOpen(false);
      await onChange();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const status = review.status || "pending";
  const tone = STATUS_TONES[status] || STATUS_TONES.pending;

  return (
    <article
      className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5"
      data-testid={`review-card-${review.id}`}
    >
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium" data-testid={`review-name-${review.id}`}>
              {review.customer_name_display || "Anonymous"}
            </span>
            <span className="inline-flex items-center gap-1 text-amber-700 font-semibold text-sm">
              <Star className="w-3.5 h-3.5 fill-amber-500 stroke-amber-500" />
              {review.rating}/5
            </span>
            {review.verified && (
              <span
                className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 inline-flex items-center gap-1"
                data-testid={`review-verified-${review.id}`}
              >
                <ShieldCheck className="w-3 h-3" /> Booking-linked
              </span>
            )}
            <span
              className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${tone}`}
              data-testid={`review-status-${review.id}`}
            >
              {status}
            </span>
            {(review.report_count || 0) > 0 && (
              <span
                className="text-[10px] px-2 py-0.5 rounded-full bg-orange-100 text-orange-900 border border-orange-200 inline-flex items-center gap-1"
                data-testid={`review-reports-${review.id}`}
              >
                <Flag className="w-3 h-3" /> {review.report_count} report{review.report_count === 1 ? "" : "s"}
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {review.created_at?.slice(0, 10)}
            {review.booking_id && (
              <>
                {" · "}
                <Link
                  to={`/app/bookings/${review.booking_id}`}
                  className="underline hover:text-slate-700"
                  data-testid={`review-booking-link-${review.id}`}
                >
                  Open linked booking <ExternalLink className="w-3 h-3 inline" />
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {review.title && (
        <h3 className="mt-3 font-semibold" data-testid={`review-title-${review.id}`}>{review.title}</h3>
      )}
      <p
        className="mt-2 text-sm text-slate-700 leading-relaxed whitespace-pre-line"
        data-testid={`review-comment-${review.id}`}
      >
        {review.comment}
      </p>

      {/* Moderation buttons */}
      <div className="mt-3 flex flex-wrap gap-2" data-testid={`review-actions-${review.id}`}>
        {status !== "published" && (
          <ModButton
            tone="emerald"
            icon={<Eye className="w-3 h-3" />}
            label="Publish"
            testId={`review-publish-${review.id}`}
            disabled={busy}
            onClick={() => moderate("published", null)}
          />
        )}
        {status !== "hidden" && (
          <ModButton
            tone="slate"
            icon={<EyeOff className="w-3 h-3" />}
            label="Hide"
            testId={`review-hide-${review.id}`}
            disabled={busy}
            onClick={() => {
              const reason = window.prompt("Reason for hiding (optional)?") || null;
              moderate("hidden", reason);
            }}
          />
        )}
        {status !== "rejected" && (
          <ModButton
            tone="rose"
            icon={<Ban className="w-3 h-3" />}
            label="Reject"
            testId={`review-reject-${review.id}`}
            disabled={busy}
            onClick={() => {
              const reason = window.prompt("Reason for rejecting (helps audit)?");
              if (reason == null) return;
              moderate("rejected", reason);
            }}
          />
        )}
        {status !== "flagged" && (
          <ModButton
            tone="orange"
            icon={<Flag className="w-3 h-3" />}
            label="Flag"
            testId={`review-flag-${review.id}`}
            disabled={busy}
            onClick={() => {
              const reason = window.prompt("Flag this review for ConfirmPH review: reason?");
              if (!reason) return;
              moderate("flagged", reason);
            }}
          />
        )}
        <ModButton
          tone="brand"
          icon={<MessageSquare className="w-3 h-3" />}
          label={review.business_reply ? "Edit reply" : "Reply"}
          testId={`review-reply-toggle-${review.id}`}
          disabled={busy}
          onClick={() => {
            setReplyText(review.business_reply || "");
            setReplyOpen((s) => !s);
          }}
        />
      </div>

      {review.moderation_reason && status !== "published" && (
        <div className="mt-2 text-[11px] text-slate-500">
          Moderation reason: {review.moderation_reason}
        </div>
      )}

      {/* Existing reply (read-only when collapsed) */}
      {review.business_reply && !replyOpen && (
        <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5"
             data-testid={`review-reply-${review.id}`}>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            Your reply
          </div>
          <p className="mt-1 text-sm text-slate-700 leading-relaxed whitespace-pre-line">
            {review.business_reply}
          </p>
          {review.business_reply_at && (
            <div className="text-[10px] text-slate-400 mt-1">
              Posted {review.business_reply_at.slice(0, 10)}
            </div>
          )}
        </div>
      )}

      {/* Reply editor */}
      {replyOpen && (
        <div className="mt-3 space-y-2" data-testid={`review-reply-editor-${review.id}`}>
          <textarea
            rows={3}
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            maxLength={2000}
            placeholder="Write a professional reply…"
            data-testid={`review-reply-textarea-${review.id}`}
            className="w-full px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm
                       focus:border-[#1B4332] focus:ring-2 focus:ring-[#1B4332]/15 outline-none"
          />
          <div className="flex flex-wrap gap-2 items-center">
            <button
              type="button"
              onClick={saveReply}
              disabled={busy}
              data-testid={`review-reply-save-${review.id}`}
              className="px-3 py-1.5 rounded-md btn-brand text-xs font-medium inline-flex items-center gap-1 disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <MessageSquare className="w-3 h-3" />}
              {review.business_reply ? "Save changes" : "Post reply"}
            </button>
            {review.business_reply && (
              <button
                type="button"
                onClick={removeReply}
                disabled={busy}
                data-testid={`review-reply-remove-${review.id}`}
                className="px-3 py-1.5 rounded-md border border-rose-300 text-rose-700 bg-white text-xs inline-flex items-center gap-1 hover:bg-rose-50 disabled:opacity-60"
              >
                <Trash2 className="w-3 h-3" /> Remove reply
              </button>
            )}
            <button
              type="button"
              onClick={() => setReplyOpen(false)}
              className="px-3 py-1.5 rounded-md border border-slate-300 bg-white text-slate-700 text-xs hover:bg-slate-50"
              data-testid={`review-reply-cancel-${review.id}`}
            >
              Cancel
            </button>
            <span className="text-[10px] text-slate-500 ml-auto">
              {replyText.length} / 2000
            </span>
          </div>
        </div>
      )}
    </article>
  );
}

const TONE_CLASSES = {
  emerald: "border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100",
  slate:   "border-slate-300   bg-white      text-slate-700   hover:bg-slate-50",
  rose:    "border-rose-300    bg-rose-50    text-rose-700    hover:bg-rose-100",
  orange:  "border-orange-300  bg-orange-50  text-orange-800  hover:bg-orange-100",
  brand:   "border-[#1B4332]   bg-[#1B4332]  text-white       hover:opacity-90",
};

function ModButton({ tone, icon, label, onClick, disabled, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`px-3 py-1.5 rounded-md border text-xs font-medium inline-flex items-center gap-1 transition-colors disabled:opacity-50 ${TONE_CLASSES[tone] || TONE_CLASSES.slate}`}
    >
      {icon} {label}
    </button>
  );
}
